/**
 * TemplatePicker — lets the practitioner choose between drill-plan templates.
 *
 * Templates:
 *  - Positional Introduction: 3 positional/floor-based drills × 3 days, Phase 1 only
 *  - Small Drill Plan: 3 corrective drills (any domain) × 3 days, Phase 1 only
 *  - Full: 11 slots × 3 days, Phases 1–3
 */

import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
  POSITIONAL_INTRO_TEMPLATE,
  SMALL_DRILL_PLAN_TEMPLATE,
  FULL_TEMPLATE,
  type DrillPlanTemplateDefinition,
} from './lib/program-structure';

interface TemplatePickerProps {
  clientPhase: 'P1' | 'P2' | 'P3' | null;
  onSelect: (template: DrillPlanTemplateDefinition) => void;
  disabled?: boolean;
}

const SlotDiagram: React.FC<{ count: number; maxShown?: number }> = ({
  count,
  maxShown = 11,
}) => {
  const shown = Math.min(count, maxShown);
  return (
    <div className="flex flex-wrap gap-1 mt-2">
      {Array.from({ length: shown }).map((_, i) => (
        <div key={i} className="w-4 h-4 rounded border border-muted-foreground/30 bg-muted/50" />
      ))}
      {count > maxShown && (
        <span className="text-[10px] text-muted-foreground self-center">+{count - maxShown}</span>
      )}
    </div>
  );
};

const TemplatePicker: React.FC<TemplatePickerProps> = ({
  clientPhase,
  onSelect,
  disabled = false,
}) => {
  const templates = [POSITIONAL_INTRO_TEMPLATE, SMALL_DRILL_PLAN_TEMPLATE, FULL_TEMPLATE];

  return (
    <TooltipProvider>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {templates.map((template) => {
          const phaseAllowed =
            !clientPhase || template.allowedPhases.includes(clientPhase);
          const isDisabled = disabled || !phaseAllowed;
          const disabledReason = !phaseAllowed
            ? `Requires ${template.allowedPhases.join(' or ')} — client is ${clientPhase}`
            : undefined;

          const card = (
            <Card
              key={template.id}
              className={cn(
                'cursor-pointer select-none transition-all border-2 active:scale-[0.99]',
                isDisabled
                  ? 'opacity-50 cursor-not-allowed border-muted'
                  : 'hover:border-primary hover:shadow-sm border-transparent'
              )}
              onClick={() => !isDisabled && onSelect(template)}
              role="button"
              tabIndex={isDisabled ? -1 : 0}
              onKeyDown={(e) => {
                if (!isDisabled && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  onSelect(template);
                }
              }}
              aria-disabled={isDisabled}
            >
              <CardHeader className="pb-1.5 pt-3 px-3">
                <div className="flex items-start justify-between gap-1.5">
                  <CardTitle className="text-sm leading-snug">{template.name}</CardTitle>
                  <div className="flex flex-wrap gap-1 justify-end">
                    {template.allowedPhases.map((p) => (
                      <Badge key={p} variant="secondary" className="text-[10px] px-1.5">
                        {p}
                      </Badge>
                    ))}
                    {template.twiceDaily && (
                      <Badge variant="outline" className="text-[10px] px-1.5">2×/day</Badge>
                    )}
                  </div>
                </div>
                <CardDescription className="text-[11px] leading-snug mt-0.5">
                  {template.description}
                  {template.preferredDomain && (
                    <span className="block mt-0.5 text-primary/70 font-medium">
                      Positional drills only
                    </span>
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent className="px-3 pb-3">
                <p className="text-[10px] text-muted-foreground">
                  {template.slots.length} slot{template.slots.length !== 1 ? 's' : ''} ×{' '}
                  {template.dayNames.length} days
                </p>
                <SlotDiagram count={template.slots.length} />
              </CardContent>
            </Card>
          );

          if (disabledReason) {
            return (
              <Tooltip key={template.id}>
                <TooltipTrigger asChild>
                  <div>{card}</div>
                </TooltipTrigger>
                <TooltipContent>{disabledReason}</TooltipContent>
              </Tooltip>
            );
          }

          return React.cloneElement(card, { key: template.id });
        })}
      </div>
    </TooltipProvider>
  );
};

export default TemplatePicker;
